import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { brandApi } from '../api/client';
import type { Brand } from '@bosamie/shared';
import {
  showToast, ConfirmDialog, Drawer, ImageUploader,
  TableSkeleton, EmptyState,
} from '../components/ui';

export function Brands() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<(Brand & { modelCount: number })[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<(Brand & { modelCount: number }) | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await brandApi.list();
      setBrands(data);
    } catch {
      showToast('加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const openCreate = () => {
    setEditId(null);
    setName('');
    setLogo('');
    setCoverUrl('');
    setDrawerOpen(true);
  };

  const openEdit = (brand: Brand) => {
    setEditId(brand.id);
    setName(brand.name);
    setLogo(brand.logo || '');
    setCoverUrl(brand.coverUrl || '');
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showToast('品牌名不能为空');
      return;
    }
    try {
      const data = { name: name.trim(), logo: logo || undefined, coverUrl: coverUrl || undefined };
      if (editId) {
        await brandApi.update(editId, data);
        showToast('保存成功');
      } else {
        await brandApi.create(data);
        showToast('创建成功');
      }
      setDrawerOpen(false);
      fetch();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '保存失败');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await brandApi.delete(deleteTarget.id);
      showToast('删除成功');
      setDeleteTarget(null);
      fetch();
    } catch {
      showToast('删除失败');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">品牌管理</div>
        <button className="btn btn-primary" onClick={openCreate}>+ 新建品牌</button>
      </div>

      {loading ? (
        <TableSkeleton rows={5} cols={4} />
      ) : brands.length === 0 ? (
        <EmptyState title="还没有品牌，开始创建吧" action={
          <button className="btn btn-primary" onClick={openCreate}>+ 新建品牌</button>
        } />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>品牌</th>
                <th>款式数</th>
                <th style={{ textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 6, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}>
                        {brand.coverUrl ? (
                          <img src={brand.coverUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
                            {brand.logo || brand.name.slice(0, 2)}
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700 }}>{brand.name}</div>
                        {brand.logo && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{brand.logo}</div>}
                      </div>
                    </div>
                  </td>
                  <td>{brand.modelCount}</td>
                  <td>
                    <div className="actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/brands/${brand.id}`)}>
                        进入款式
                      </button>
                      <button className="btn-icon" onClick={() => openEdit(brand)} title="编辑">✎</button>
                      <button className="btn-icon danger" onClick={() => setDeleteTarget(brand)} title="删除">🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer */}
      {drawerOpen && (
        <Drawer
          title={editId ? '编辑品牌' : '新建品牌'}
          onClose={() => setDrawerOpen(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setDrawerOpen(false)}>取消</button>
              <button className="btn btn-primary" onClick={handleSave}>保存</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">品牌名 <span className="req">*</span></label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="如 Bosamie" />
          </div>
          <div className="form-group">
            <label className="form-label">标识文字</label>
            <input className="form-input" value={logo} onChange={(e) => setLogo(e.target.value)} placeholder="如 BO（选填）" maxLength={20} />
          </div>
          <div className="form-group">
            <label className="form-label">封面图</label>
            <ImageUploader
              files={coverUrl ? [{ url: coverUrl }] : []}
              onAdd={(url) => setCoverUrl(url)}
              onRemove={() => setCoverUrl('')}
            />
          </div>
        </Drawer>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <ConfirmDialog
          title="删除品牌"
          danger
          body={`确定要删除「${deleteTarget.name}」吗？将级联删除其下所有款式与颜色，此操作不可撤销。`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
